import useGlobalStore from '@/store/useGlobalStore'
import { useEffect, useState } from 'react'
import type { HydraErrorType } from '@/lib/services/Hydra/_hydraError'
import type { OrderItem } from '@/lib/services/Hydra/quotesCallType'
import { hydra } from '@/lib/services/Hydra'
import { getAlpha2WithExceptions } from '@/components/Checkout/_helpers/functions/getAlpha2WithExceptions'
import type { SupportedAlpha2CountryCode } from '@/lib/utils/localization/getSupportedAlpha2CountryCodes'
import { useSignOutUser } from './useSignOutUser'

export type OrderCalculationType = {
  isLoadingOrderCalculation: boolean
  orderCalculation: OrderItem | null
  orderCalculationError: HydraErrorType | null
}

export const useOrderCalculationSideCart = (): OrderCalculationType => {
  const {
    app,
    user,
    cart: { otp, subscribe },
    setAddedLines,
  } = useGlobalStore()
  const { signOutUser } = useSignOutUser()
  const [isLoadingOrderCalculation, setIsLoadingOrderCalculation] =
    useState(false)
  const [orderCalculationError, setOrderCalculationError] =
    useState<HydraErrorType | null>(null)
  const [orderCalculation, setOrderCalculation] = useState<OrderItem | null>(
    null,
  )

  const calculateOrder = async () => {
    // This is hardcode because if we use an actual method given back from the api,
    // the quotes call to get the free items fails (go figure), we we're stuck with this.
    const shipMethodString = `type=${
      app.marketExceptions?.defaultEconomyShippingMethod || 'Economy'
    }`

    setIsLoadingOrderCalculation(true)
    setOrderCalculationError(null)

    const cartProducts = [...otp, ...subscribe]

    if (!cartProducts || cartProducts.length === 0) {
      setIsLoadingOrderCalculation(false)
      return null
    }

    const calculatedOrder = await hydra.calculateBasicOrder({
      customerType: user.customerType || 'Customer',
      customerId: user.customer?.id.unicity || '',
      items: cartProducts.map((product) => ({
        id: product.product.sku || '',
        quantity: product.qty,
        currency: app.marketExceptions?.currencyCode || 'USD',
        po: product.po || '',
      })),
      shippingMethod: shipMethodString,
      customer: user.customer,
      shipToAddress: {
        country: getAlpha2WithExceptions({ alpha2: app.alpha2 }),
      },
      market: getAlpha2WithExceptions({
        alpha2: app.alpha2,
      }) as SupportedAlpha2CountryCode,
      token: user.token,
    })

    setIsLoadingOrderCalculation(false)
    return calculatedOrder
  }

  useEffect(() => {
    ;(async () => {
      const result = await calculateOrder()

      if (!result) return

      if (result.error) {
        setOrderCalculationError(result)
        if (result.error.code === 401) {
          await signOutUser()
        }
        return
      }

      if (!result.items) {
        setOrderCalculationError({
          error: {
            message: 'Unexpected Error',
          },
        })
        return
      }

      setAddedLines(result.items[0].added_lines)
      setOrderCalculation(result.items[0])
      setOrderCalculationError(null)
      setIsLoadingOrderCalculation(false)
    })()
  }, [
    app.alpha2,
    user.customer,
    user.token,
    otp,
    subscribe,
    ...otp.map((item) => item.qty).join(','),
    ...subscribe.map((item) => item.qty).join(','),
  ])

  return { isLoadingOrderCalculation, orderCalculation, orderCalculationError }
}
